import axios from 'axios';
import { useState, useEffect, useRef, useCallback } from 'react';
import { FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const SearchBar = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const resultsRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);
  const cancelToken = useRef(null);
  const navigate = useNavigate();

  // Memoized fetch function with cancellation
  const fetchProducts = useCallback(async (query) => {
    if (!query?.trim() || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    // Cancel previous request if exists
    if (cancelToken.current) {
      cancelToken.current.cancel('Operation canceled due to new request');
    }
    cancelToken.current = axios.CancelToken.source();

    setIsLoading(true);
    try {
      const { data } = await axios.get('/api/products/search', {
        params: { q: query.trim() },
        cancelToken: cancelToken.current.token
      });
      setSearchResults(Array.isArray(data) ? data : data?.data || []);
      setHighlightedIndex(-1);
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Search error:', error);
        setSearchResults([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Cleanup timers and requests on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (cancelToken.current) {
        cancelToken.current.cancel('Component unmounted');
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowResults(true);
    
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Set new timer
    debounceTimer.current = setTimeout(() => {
      fetchProducts(value);
    }, 300);
  };

  const scrollIntoView = (index) => {
    if (resultsRef.current && index >= 0) {
      const items = resultsRef.current.querySelectorAll('li');
      if (items[index]) {
        items[index].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  };

  const navigateToProduct = (productId) => {
    navigate(`/singleproduct/${productId}`);
    setShowResults(false);
   // setSearchTerm('');
  };

  const handleResultClick = (product) => {
    navigateToProduct(product.id);
    setSearchTerm(product.name);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) return;

    const handledKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'];
    if (handledKeys.includes(e.key)) {
      e.preventDefault();
    }

    switch (e.key) {
      case 'ArrowDown': {
        const newIndex = Math.min(highlightedIndex + 1, searchResults.length - 1);
        setHighlightedIndex(newIndex);
        scrollIntoView(newIndex);
        break;
      }
      case 'ArrowUp': {
        const newIndex = Math.max(highlightedIndex - 1, -1);
        setHighlightedIndex(newIndex);
        scrollIntoView(newIndex);
        break;
      }
      case 'Enter':
        if (highlightedIndex >= 0) {
          handleResultClick(searchResults[highlightedIndex]);
          setSearchTerm(searchResults[highlightedIndex].name);
        }
        break;
      case 'Escape':
        setShowResults(false);
        inputRef.current?.focus();
        break;
      default:
        return;
    }
  };

  return (
    <div className="order-3 relative xl:col-span-5 lg:col-span-5 md:col-span-5 sm:col-span-2 col-span-3 mr-0 lg:block xl:block md:block sm:mt-2 lg:mt-0 xl:mt-0 md:mt-0 mt-2 ">
      <input
        ref={inputRef}
        type="text"
        className="rounded-full bg-gray-100 xl:p-3.5 lg:p-3.5 md:p-2 p-3 lg:text-[16px] md:text-[14px] xl:text[16px] text-[10px] pl-5 w-full"
        placeholder="Search everything at store online and offline"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowResults(true)}
        onBlur={() => setTimeout(() => setShowResults(false), 200)}
      />
      <div className="absolute xl:top-2 lg:top-2 md:top-1 sm:top-1 top-1 right-0 flex items-center pr-3">
        <div className="xl:h-9 xl:w-9 lg:h-9 lg:w-9 md:h-7 md:w-7 w-7 text-white bg-blue-950 p-2 rounded-full">
          <FaSearch className="font-bold xl:size-5 lg:size-5 md:size-3 size-3" />
        </div>
      </div>

      {/* Search Results Dropdown */}
      {showResults && searchTerm && (
        <div 
          ref={resultsRef}
          className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md max-h-60 overflow-auto"
        >
          {isLoading ? (
            <div className="p-2 text-gray-500">Loading...</div>
          ) : searchResults.length > 0 ? (
            <ul>
              {searchResults.map((product, index) => (
                <li
                  key={product.id}
                  className={`p-2 cursor-pointer ${
                    highlightedIndex === index 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => handleResultClick(product)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {product.name}
                </li>
              ))}
            </ul>
          ) : searchTerm.trim().length >= 2 ? (
            <div className="p-2 text-gray-500">No products found</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SearchBar;